import { useEffect } from "react";
import { useNavigate, useRoutes } from "react-router-dom";
import { CssBaseline } from "@mui/material";

import routes from "./router";
import ThemeProvider from "./theme/ThemeProvider";
import ErrorBoundary from "src/components/ErrorBoundary";
import ConfigBanner from "src/components/ConfigBanner";
import CommandPalette from "src/components/CommandPalette";
import ShortcutCheatSheet from "src/components/ShortcutCheatSheet";
import SnackbarProvider from "src/components/SnackbarProvider";
import { ReleaseNotesGate } from "src/components/ReleaseNotesModal";
import EmailLoginDialog from "src/content/pages/Auth/EmailLogin";
import useGlobalShortcuts from "src/hooks/useGlobalShortcuts";
import { syncdown } from "src/handlers/Sync";
import { loadCurrentSession } from "src/handlers/Account";
import { ensureActiveProjectSession } from "src/handlers/Projects";
import {
  SNAPSHOT_ARRAY_LENGTH,
  normalizeProjectSnapshot,
  safeParseJson
} from "src/handlers/Projects/schema";
import { installErrorReporter, reportError } from "src/handlers/errorReporter";

const ensureArrayStorage = (key, times = SNAPSHOT_ARRAY_LENGTH) => {
  const existing = safeParseJson(localStorage.getItem(key), null);

  if (!Array.isArray(existing)) {
    const array = Array.from({ length: times }, () => ({}));
    localStorage.setItem(key, JSON.stringify(array));
  }
};

function bootstrapStorage() {
  // The release notes are now driven by the global ReleaseNotesGate +
  // `src/handlers/changelog.js`, which uses the `lastSeenReleaseVersion`
  // localStorage key. The legacy `version` storage marker that used to
  // gate the upgrade modal is no longer consumed and is intentionally
  // not written here.

  // One-shot cleanup: older clients wrote a `sessionid` key alongside
  // `projectid`. New code only reads the legacy key as a fallback, so
  // remove it once on startup to stop drifting state.
  if (sessionStorage.getItem("sessionid")) {
    sessionStorage.removeItem("sessionid");
  }

  const normalizedSnapshot = normalizeProjectSnapshot({
    router_data: safeParseJson(localStorage.getItem("router_data"), null),
    router_final: safeParseJson(localStorage.getItem("router_final"), null),
    switch_data: safeParseJson(localStorage.getItem("switch_data"), null),
    switch_final: safeParseJson(localStorage.getItem("switch_final"), null),
    vlan_data: safeParseJson(localStorage.getItem("vlan_data"), null)
  });

  localStorage.setItem("router_data", JSON.stringify(normalizedSnapshot.router_data));
  localStorage.setItem("switch_data", JSON.stringify(normalizedSnapshot.switch_data));
  localStorage.setItem("vlan_data", JSON.stringify(normalizedSnapshot.vlan_data));
  ensureArrayStorage("router_final");
  ensureArrayStorage("switch_final");
}

// Run once at module load so other imports (e.g. ThemeProvider) can rely on
// storage being normalized. Safe to call multiple times.
bootstrapStorage();
installErrorReporter();

const App = () => {
  const content = useRoutes(routes);
  const navigate = useNavigate();

  // register global keyboard shortcuts once at the app root.
  useGlobalShortcuts();

  // Wire `netbase:navigate` events from `g <letter>` shortcuts (and any
  // future imperative dispatches) into react-router's navigate.
  useEffect(() => {
    const onNavigate = (event) => {
      const target = event?.detail?.to;
      if (typeof target === "string" && target) {
        navigate(target);
      }
    };
    window.addEventListener("netbase:navigate", onNavigate);
    return () => window.removeEventListener("netbase:navigate", onNavigate);
  }, [navigate]);

  // Phase I2: when any write observes a 401 the sync layer dispatches
  // `netbase:authExpired`. We surface the existing email login dialog in
  // re-auth mode so the user can recover without losing in-memory editor
  // state, and flush pending writes once they're back in.
  useEffect(() => {
    const onAuthExpired = () => {
      window.dispatchEvent(
        new CustomEvent("netbase:showEmailLogin", { detail: { reAuth: true } })
      );
    };
    window.addEventListener("netbase:authExpired", onAuthExpired);
    return () => window.removeEventListener("netbase:authExpired", onAuthExpired);
  }, []);

  useEffect(() => {
    let mounted = true;

    (async () => {
      await loadCurrentSession();
      const project = await ensureActiveProjectSession();
      if (!mounted || !project) return;

      await Promise.all([syncdown("router"), syncdown("vlan"), syncdown("switch")]);
    })().catch((error) => {
      reportError("app.bootstrap", error);
    });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <ThemeProvider>
      <CssBaseline />
      <ConfigBanner />
      <SnackbarProvider>
        <ErrorBoundary>
          {content}
          <EmailLoginDialog />
          <CommandPalette />
          <ShortcutCheatSheet />
          {/* Global release-notes gate — detects unseen builds and shows the
              changelog modal regardless of which page the user is on. */}
          <ReleaseNotesGate />
        </ErrorBoundary>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;
